import { DATE_FORMAT } from '@business/constants/time';
import { OrgResponse, RefundFormResponse, RefundLineResponse, TransactionType } from '@business/entities/organization';
import { SalesInvoiceType, SalesStatusType, InvoiceTypeEnum } from '@business/entities/TransactionInvoice';
import { CommonService, OrgService, StripeService, IntegrationService, UserService } from '@business/services';
import { FormSelect } from '@core/components/form';
import { formControl, formGroup } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { tapError } from '@core/utils/rxjs/operators';
import { MDBDataTable } from 'mdbreact';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { Badge, Card, CardBody, CardTitle, DropdownMenu, DropdownToggle, Spinner, UncontrolledDropdown } from 'reactstrap';
import { tap } from 'rxjs/operators';
import { OrganizationPageBloc } from '../../OrganizationPageBloc';
import { openConvertProjectModal } from './ConvertProjectModal';
import { openProcessPaymentModal, openRefundPaymentModal } from './ProcessPaymentModal';
import { FlashMessage } from '@core/components/FlashMessage';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { openCopyLinkModal } from './CopyLinkModal';
import { WithTooltip } from '@modules/common';
import { HasPermission } from '@core/components/HasPermission';
import { GCTable } from '@core/components/GCTable';
import { Session } from '@modules/auth/session';
import { isTenants } from '@business/services/common_service/common_service';

function defineSalesPageForm() {
  return formGroup({
    status: formControl<SalesStatusType>({
      placeholder: 'Select Status',
    }),
    type: formControl<SalesInvoiceType>({
      placeholder: 'Select Type',
    }),
  });
}
export interface SalesProps {
  organization: OrgResponse;
  bloc: OrganizationPageBloc;
}

export const Sales = memo((props: SalesProps) => {
  const { organization, bloc } = props;
  const [isSyncing, setSync] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  const fullLoading = useLoadingState(bloc.fullLoading);

  const form = useMemo(() => defineSalesPageForm(), []);
  const formValue = useNonNilObservable(form.value$);

  const transactions = organization.transactions;
  const invoices = organization.invoices;
  const authUser = useObservable(Session.user$);

  const statusItems = useMemo(() => CommonService.selectItems(OrgService.getAllSalesStatus(), s => R.capitalizeWords(s)), []);
  const typeItems = useMemo(
    () =>
      CommonService.selectItems(OrgService.getAllSalesTypes(), s => (s === SalesInvoiceType.salesReceipt ? 'Sales Receipt' : R.capitalizeWords(s))),
    [],
  );

  const syncPayments = useCallback(() => {
    setSync(true);
    bloc
      .syncPayments(organization.id)
      .pipe(
        tap(() => setSync(false)),
        tapError(() => setSync(false)),
      )
      .subscribe();
  }, [bloc, organization.id]);

  const showRefundButton = useMemo(
    () => isTenants(['1715030651', '1673302957', '1645143439']) && ['hayder@studio98.com', 'sarmad@studio98.com'].includes(Session.user.email),
    [],
  );

  const data = useMemo(
    () => ({
      columns: [
        {
          label: `Date`,
          field: 'date',
        },
        {
          label: `${gcLabel('Project')}/Subscription`,
          field: 'title',
        },
        {
          label: 'Type',
          field: 'type',
        },
        {
          label: 'Due Date',
          field: 'dueDate',
          sort: 'desc',
        },
        {
          label: 'Total',
          field: 'amount',
          sort: 'desc',
        },
        {
          label: 'Balance',
          field: 'balance',
          sort: 'desc',
        },
        {
          label: 'Status',
          field: 'status',
        },
        {
          label: '',
          field: 'actions',
        },
      ],

      rows: R.sortBy(
        R.concat(
          R.unionBy(transactions, t => [t.invoiceId, t.chargeId].join('-'))
            .filter(t => {
              if (R.isEmpty(formValue.status) && R.isEmpty(formValue.type)) {
                return t;
              }
              if (!R.isEmpty(formValue.type) && formValue.type != t.invoiceType) {
                return false;
              }
              if (!R.isEmpty(formValue.status) && formValue.status != t.status) {
                return false;
              }
              return t;
            })
            .map(row => {
              const transactionInvoices = R.filter(invoices, i => R.toNumber(i.id) === R.toNumber(row.invoiceId));
              const transactionInvoice = R.head(transactionInvoices);
              return {
                // ...row,
                title: (
                  <div>
                    <span>{row.title}</span>
                  </div>
                ),
                date: Formatter.formatTime(row.paymentTime, DATE_FORMAT, { utc: true, utcOffset: 0 }),
                status: (
                  <>
                    {row.method !== 'stripe' && (
                      <Badge pill color={OrgService.getSalesStatusIntent(row.status)} className={`badge-soft font-size-12 px-2 py-1`}>
                        {R.capitalizeWords(row.status)}
                      </Badge>
                    )}
                    {row.method === 'stripe' && (
                      <Badge pill color={StripeService.getTransactionStatusColor(row.status)} className={`badge-soft font-size-12 px-2 py-1`}>
                        {R.capitalizeWords(row.status)}
                      </Badge>
                    )}
                  </>
                ),
                //   type: <span>{R.capitalizeWords(row.type)}</span>,
                type: R.capitalizeWords(row.invoiceType),
                method: row.method === 'quickbooks' ? 'QuickBooks' : R.capitalizeWords(row.method),
                amount: Formatter.formatCurrency(row.amount, organization.currency),
                balance: row.invoiceType === SalesInvoiceType.invoice ? Formatter.formatCurrency(row.amount, organization.currency) : '',
                dueDate: <span></span>,
                paymentTime: row.paymentTime,
                actions: (
                  <UncontrolledDropdown>
                    <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
                      <i className="bx bx-dots-horizontal-rounded"></i>
                    </DropdownToggle>

                    {row.type === TransactionType.oneTime &&
                      (row.invoiceType === SalesInvoiceType.invoice || row.invoiceType === SalesInvoiceType.salesReceipt) && (
                        <DropdownMenu container="body" className="dd-menu_outside">
                          <a className={`dropdown-item`} onClick={() => openConvertProjectModal({ organization, transaction: row })}>
                            Convert to {gcLabel('Project')}
                          </a>
                        </DropdownMenu>
                      )}

                    {UserService.isAdmin(authUser) && row.showRefund && (
                      <DropdownMenu container="body" className="dd-menu_outside">
                        <a
                          className={`dropdown-item`}
                          onClick={() =>
                            openRefundPaymentModal({
                              transaction: row,
                              transactionInvoice,
                              onSubmit: (transactionId, data: RefundFormResponse) => bloc.refundPayment(transactionId, data),
                            })
                          }
                        >
                          Refund payment
                        </a>
                      </DropdownMenu>
                    )}
                  </UncontrolledDropdown>
                ),
              };
            }),
          R.map(
            invoices.filter(t => {
              if (R.isEmpty(formValue.status) && R.isEmpty(formValue.type)) {
                return t;
              }
              if (!R.isEmpty(formValue.type) && formValue.type != t.invoiceType) {
                return false;
              }
              if (!R.isEmpty(formValue.status) && formValue.status != t.status) {
                return false;
              }
              return t;
            }),
            row => ({
              title: (
                <div>
                  <span>{row.title}</span>
                  {R.isEmpty(row.importId) && IntegrationService.isQBEnabled() && (
                    <WithTooltip tooltip="Invoice not synced with QBO">
                      <span className="text-danger ml-2 hover-pointer">
                        <i className="fa fa-exclamation-triangle font-size-16" />
                      </span>
                    </WithTooltip>
                  )}
                </div>
              ),
              date: Formatter.formatTime(row.invoiceDate, DATE_FORMAT, { utc: true, utcOffset: 0 }),
              paymentTime: row.invoiceDate,
              dueDate: (
                <span className={row.status === SalesStatusType.overdue ? 'text-danger' : ''}>
                  {Formatter.formatTime(row.dueDate, DATE_FORMAT, { utc: true, utcOffset: 0 })}
                </span>
              ),
              status: (
                <Badge pill color={OrgService.getSalesStatusIntent(row.status)} className={`badge-soft font-size-12 px-2 py-1`}>
                  {R.capitalizeWords(row.status)}
                </Badge>
              ),
              //   type: <span>{R.capitalizeWords(row.type)}</span>,
              type: `${R.capitalizeWords(R.replace(row.invoiceType, '-', ' '))} ${row.paidVia ? `(${row.paidVia})` : ''}`,
              method: row.method === 'quickbooks' ? 'QuickBooks' : R.capitalizeWords(row.method),
              amount: Formatter.formatCurrency(
                [InvoiceTypeEnum.refund, InvoiceTypeEnum.creditMemo].includes(row.type) && row.amount != 0 ? -row.amount : row.amount,
                organization.currency,
              ),
              balance: Formatter.formatCurrency(row.remaining, organization.currency),
              actions: (
                <span>
                  <HasPermission module={'organizations'} moduleKey={'transactions'}>
                    <UncontrolledDropdown>
                      <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
                        <i className="bx bx-dots-horizontal-rounded"></i>
                      </DropdownToggle>

                      <DropdownMenu container="body" className="dd-menu_outside">
                        <>
                          {OrgService.canProcessInvoice(row) && (
                            <a
                              className={`dropdown-item`}
                              onClick={() =>
                                openProcessPaymentModal({
                                  max: row.remaining,
                                  methods: organization.methods,
                                  onSubmit: (paymentId, amount) => bloc.processPaymentOrg(row.id, paymentId, amount),
                                  currency: organization.currency,
                                })
                              }
                            >
                              Process Payment
                            </a>
                          )}
                          <a className={`dropdown-item`} onClick={() => bloc.sendInvoiceOrg(row.id).subscribe()}>
                            Send Invoice
                          </a>

                          <a
                            className={`dropdown-item`}
                            onClick={() => {
                              openCopyLinkModal({
                                invoiceId: row.id,
                                url: row.url,
                                balance: R.toNumber(row.balance),
                                amount: row.amount,
                                currency: organization.currency,
                              });
                              // CommonService.copyToClipboard(row.url);
                              // FlashMessage.success('Link Copied', 'Payment link has been copied to your clipboard');
                            }}
                          >
                            Copy Payment Link
                          </a>
                          <a
                            className={`dropdown-item`}
                            onClick={() => {
                              setDownloading(true);
                              bloc
                                .downloadInvoice(row.id, `Invoice #${row.id}`)
                                .pipe(
                                  tap(() => setDownloading(false)),
                                  tapError(() => setDownloading(false)),
                                )
                                .subscribe();
                            }}
                          >
                            Download Invoice {downloading && <Spinner className="ml-2" size="sm" color="primary" />}
                          </a>
                          {R.isEmpty(row.importId) && IntegrationService.isQBEnabled() && (
                            <a
                              className={`dropdown-item`}
                              onClick={() => {
                                setSync(true);
                                bloc
                                  .syncInvoice(row.id)
                                  .pipe(
                                    tap(() => setSync(false)),
                                    tapError(() => setSync(false)),
                                  )
                                  .subscribe();
                              }}
                            >
                              Sync Invoice with QBO {isSyncing && <Spinner className="ml-2" size="sm" color="primary" />}
                            </a>
                          )}
                          <div className="dropdown-divider"></div>
                          <a className={`dropdown-item text-danger`} onClick={() => bloc.voidInvoiceOrg(row.id).subscribe()}>
                            Void Invoice
                          </a>
                        </>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </HasPermission>
                </span>
              ),
            }),
          ),
        ),
        row => -row.paymentTime,
      ),
    }),
    [bloc, downloading, formValue.status, formValue.type, invoices, isSyncing, organization, transactions],
  );

  const columns = useMemo(
    () => [
      {
        name: `Date`,
        sortField: 'date',
        width: '110px',
        cell: row => row.date,
      },
      {
        name: `Project/Subscription`,
        sortField: 'title',
        cell: row => row.title,
      },
      {
        name: `Type`,
        sortField: 'type',
        cell: row => row.type,
      },
      {
        name: `Due Date`,
        width: '110px',
        sortField: 'dueDate',
        cell: row => row.dueDate,
      },
      {
        name: `Total`,
        sortField: 'amount',
        cell: row => row.amount,
      },
      {
        name: `Balance`,
        sortField: 'balance',
        cell: row => row.balance,
      },
      {
        name: `Status`,
        width: '90px',
        sortField: 'status',
        cell: row => row.status,
      },
      {
        name: ``,
        width: '50px',
        sortField: 'actions',
        cell: row => row.actions,
      },
    ],
    [],
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          <div className="d-flex align-items-center">
            Transactions
            {fullLoading.loading && <Spinner className="ml-2 font-size-12" size="sm" color="primary" />}
          </div>

          <div>
            <div className="d-flex">
              <div style={{ minWidth: 150 }}>
                <FormSelect withoutGroup isClearable control={form.controls.status} items={statusItems} />
              </div>
              <div className="ml-2" style={{ minWidth: 150 }}>
                <FormSelect withoutGroup isClearable control={form.controls.type} items={typeItems} />
              </div>
              <div className="ml-2 mt-2">
                {!!isSyncing && <Spinner className="ml-2" size="sm" color="primary" />}
                {!isSyncing && <i className="bx bx-sync font-size-20 text-primary" onClick={syncPayments} style={{ cursor: 'pointer' }}></i>}
              </div>
            </div>
          </div>
        </CardTitle>
        {!fullLoading.loading && (
          <Fragment>
            <GCTable responsive noSearch noHeader columns={columns} data={data.rows} paginationPerPage={10} />
            {/* <MDBDataTable
              responsive
              striped
              bordered
              data={{ columns: data.columns, rows: [...data.rows] }}
              noBottomColumns
              sortable={false}
              // order={['paymentTime', 'desc']}
              info={false}
              paging
              searching={false}
              paginationLabel={['Prev', 'Next']}
            /> */}
          </Fragment>
        )}
      </CardBody>
    </Card>
  );
});
